import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Search, Trash2, Edit, Send, Mail, MessageCircle, MessageSquare, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CHANNEL_ICONS = { email: Mail, whatsapp: MessageCircle, sms: MessageSquare };
const CHANNEL_COLORS = { email: 'text-amber-400', whatsapp: 'text-green-400', sms: 'text-blue-400' };

const StatusBadge = ({ status }) => {
  const map = {
    draft:     { cls: 'bg-slate-500/20 text-slate-300 border-slate-500/30', icon: Edit, label: 'Draft' },
    scheduled: { cls: 'bg-blue-500/20 text-blue-300 border-blue-500/30', icon: Clock, label: 'Scheduled' },
    sending:   { cls: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30', icon: RefreshCw, label: 'Sending' },
    completed: { cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', icon: CheckCircle, label: 'Completed' },
    failed:    { cls: 'bg-red-500/20 text-red-300 border-red-500/30', icon: XCircle, label: 'Failed' },
  };
  const cfg = map[status] || map.draft;
  const Icon = cfg.icon;
  return <Badge className={cfg.cls}><Icon className="w-3 h-3 mr-1" />{cfg.label}</Badge>;
};

const BroadcastsListPage = () => {
  const navigate = useNavigate();
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/broadcasts`, { headers });
      setBroadcasts(r.data.broadcasts || []);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id, name) => {
    if (!window.confirm(`Delete broadcast "${name}"?`)) return;
    try {
      await axios.delete(`${API}/broadcasts/${id}`, { headers });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Delete failed: ' + (e?.response?.data?.detail || e.message)); }
  };

  const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };

  const filtered = broadcasts.filter(b => !search || (b.name || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="broadcasts-list">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-pink-500/30 to-orange-500/30 flex items-center justify-center">
            <Send className="w-5 h-5 text-pink-300" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-white">Broadcasts</h1>
            <p className="text-sm text-gray-400">One-off multi-channel campaigns to segments, lists, or manual audiences.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56 bg-wt-bg" />
          </div>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          <Button onClick={() => navigate('/marketing/broadcasts/new')} className="bg-gradient-to-r from-pink-500 to-orange-500 hover:opacity-90" data-testid="new-broadcast-btn">
            <Plus className="w-4 h-4 mr-1" /> New Broadcast
          </Button>
        </div>
      </div>

      <div className="bg-wt-surface border border-wt-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-wt-paper border-b border-wt-border">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Channels</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Audience</th>
                <th className="px-4 py-3">Sent</th>
                <th className="px-4 py-3">Sent At</th>
                <th className="px-4 py-3 w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-gray-500">
                  {search ? 'No broadcasts match your search.' : 'No broadcasts yet. Click "New Broadcast" to send one.'}
                </td></tr>
              )}
              {filtered.map(b => (
                <tr key={b.id} className="border-b border-wt-border hover:bg-wt-paper/50 transition" data-testid={`bc-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/marketing/broadcasts/${b.id}`)} className="text-left text-white hover:text-pink-300 font-medium">{b.name}</button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {(b.channels || []).map(ch => {
                        const Icon = CHANNEL_ICONS[ch] || Send;
                        return <Icon key={ch} className={`w-4 h-4 ${CHANNEL_COLORS[ch] || ''}`} />;
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                  <td className="px-4 py-3 text-gray-300">{(b.stats?.audience_size ?? 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-300">{(b.stats?.sent ?? 0).toLocaleString()} / {(b.stats?.audience_size ?? 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-400">{fmtDate(b.launched_at || b.scheduled_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/marketing/broadcasts/${b.id}`)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(b.id, b.name)} data-testid={`del-bc-${b.id}`}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BroadcastsListPage;
